/**
 * Outlook/Google-style meeting card for an inbound email that carried a
 * calendar invite: when (in your zone and the organizer's), where, a Join
 * button for Teams/Zoom/Meet links, the guest list with responses, and
 * Yes / Maybe / No that emails the organizer a real iTIP reply.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, CircleHelp, ExternalLink, MapPin, Users, X } from "lucide-react";
import { Button, toast } from "@/ui";
import {
    messageInvites,
    type Message,
    type MessageInviteState,
    type RsvpResponse,
    type RsvpStatus,
} from "../lib/api";
import { findJoinUrl, fmt, formatRange, inviteQueryKey, localZone, patchInviteCaches } from "../lib/calendar-invite";

const RESPONSE_LABEL: Record<RsvpStatus, string> = {
    accepted: "Going",
    tentative: "Maybe",
    declined: "Not going",
    needs_action: "Awaiting reply",
};

function StatusIcon({ status }: { status: RsvpStatus }) {
    if (status === "accepted") return <Check className="invite-status invite-status--accepted" aria-label="Accepted" />;
    if (status === "declined") return <X className="invite-status invite-status--declined" aria-label="Declined" />;
    if (status === "tentative") return <CircleHelp className="invite-status invite-status--tentative" aria-label="Maybe" />;
    return <span className="invite-status invite-status--pending" aria-label="Awaiting reply" />;
}

export default function InviteCard({ message }: { message: Message }) {
    const invite = message.calendarInvite!;
    const queryClient = useQueryClient();
    const [noteOpen, setNoteOpen] = useState(false);
    const [note, setNote] = useState("");
    const [notify, setNotify] = useState(true);

    const query = useQuery({
        queryKey: inviteQueryKey(invite.uid, message.id),
        queryFn: () => messageInvites.get(message.id),
        staleTime: 30_000,
    });
    const state = query.data && query.data.ok ? query.data as MessageInviteState : null;

    const respond = useMutation({
        mutationFn: (response: RsvpResponse) => messageInvites.respond({
            messageId: message.id,
            response,
            comment: note.trim() || undefined,
            notifyOrganizer: notify,
        }),
        // Light the chosen button (and our row in the guest list) immediately.
        onMutate: async (response) => {
            const key = inviteQueryKey(invite.uid, message.id);
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient.getQueryData<MessageInviteState | { ok: false; error: string }>(key);
            if (previous?.ok) {
                const me = previous.myEmail;
                queryClient.setQueryData<MessageInviteState>(key, {
                    ...previous,
                    myResponse: response,
                    attendees: previous.attendees.map(a => a.email.toLowerCase() === me ? { ...a, rsvpStatus: response } : a),
                });
            }
            return { previous, key };
        },
        onSuccess: (result, response, context) => {
            if (!result.ok) {
                if (context) queryClient.setQueryData(context.key, context.previous);
                toast.error(result.error === "reply_failed"
                    ? "Couldn't email the organizer — your response wasn't saved. Try again."
                    : `Couldn't respond (${result.error}).`);
                return;
            }
            queryClient.setQueryData(inviteQueryKey(invite.uid, message.id), result);
            // Other cards for the same meeting (forwards, the thread's later
            // copies) take the new guest list too.
            patchInviteCaches(queryClient, { uid: invite.uid, meeting: result.meeting, attendees: result.attendees });
            setNoteOpen(false);
            setNote("");
            toast.success(result.replySent
                ? `${RESPONSE_LABEL[response]} — ${invite.organizer?.name || invite.organizer?.email || "organizer"} has been told.`
                : `${RESPONSE_LABEL[response]} — saved without notifying the organizer.`);
        },
        onError: (error: any, _response, context) => {
            if (context) queryClient.setQueryData(context.key, context.previous);
            toast.error(error?.message ?? "Couldn't respond.");
        },
    });

    // Prefer the live meeting (a later email may have moved it) over the snapshot.
    const startAt = state?.meeting?.startAt ?? invite.startAt;
    const endAt = state?.meeting?.endAt ?? invite.endAt;
    const eventZone = state?.meeting?.timeZone ?? invite.timeZone ?? "UTC";
    const title = state?.meeting?.title ?? invite.summary ?? "(no title)";
    const location = state?.meeting?.location ?? invite.location;
    const joinUrl = findJoinUrl({ location, description: invite.description });
    const myZone = localZone();
    const cancelled = invite.method === "CANCEL" || state?.meeting?.status === "cancelled";
    const isRequest = invite.method === "REQUEST";
    const canRespond = isRequest && !cancelled && !state?.superseded && Boolean(state?.myEmail ?? invite.recipient);
    const myResponse = state?.myResponse ?? "needs_action";

    const myEmail = (state?.myEmail ?? invite.recipient ?? "").toLowerCase();
    const guests = state?.attendees.length
        ? state.attendees
        : [
            ...(invite.organizer ? [{ email: invite.organizer.email, name: invite.organizer.name, role: "organizer", rsvpStatus: "accepted" as RsvpStatus, rsvpComment: null }] : []),
            ...invite.attendees.map(a => ({ email: a.email, name: a.name, role: "attendee", rsvpStatus: "needs_action" as RsvpStatus, rsvpComment: null })),
        ];
    const counts = guests.reduce((acc, g) => { acc[g.rsvpStatus] = (acc[g.rsvpStatus] ?? 0) + 1; return acc; }, {} as Record<string, number>);

    const start = startAt ? new Date(startAt) : null;

    return (
        <section className={`invite-card${cancelled ? " invite-card--cancelled" : ""}`} aria-label="Meeting invitation">
            <div className="invite-card-head">
                <div className="invite-date-tile" aria-hidden>
                    <span>{start ? fmt(start.toISOString(), myZone, { month: "short" }) : "—"}</span>
                    <strong>{start ? fmt(start.toISOString(), myZone, { day: "numeric" }) : "?"}</strong>
                </div>
                <div className="invite-card-main">
                    <div className="invite-kicker">
                        <CalendarClock aria-hidden />
                        {cancelled ? "Meeting cancelled" : invite.method === "REQUEST" ? "Invitation" : invite.method === "REPLY" ? "Response" : "Calendar event"}
                        {state?.superseded && !cancelled && <span className="invite-flag">Updated since — showing latest</span>}
                    </div>
                    <h3 className="invite-title">{title}</h3>
                    {startAt
                        ? <p className="invite-when">
                            {formatRange(startAt, endAt, myZone, invite.allDay)}
                            {!invite.allDay && eventZone !== myZone && eventZone !== "UTC" && (
                                <span className="invite-when-alt">{formatRange(startAt, endAt, eventZone, false)} (organizer)</span>
                            )}
                        </p>
                        : <p className="invite-when muted">Time not specified</p>}
                </div>
                {joinUrl && !cancelled && (
                    <a className="invite-join" href={joinUrl} target="_blank" rel="noreferrer noopener">
                        Join <ExternalLink aria-hidden />
                    </a>
                )}
            </div>

            <dl className="invite-details">
                {location && <>
                    <dt><MapPin aria-label="Location" /></dt>
                    <dd>{location}</dd>
                </>}
                <dt><Users aria-label="Guests" /></dt>
                <dd>
                    <div className="invite-guest-summary">
                        {guests.length} {guests.length === 1 ? "guest" : "guests"}
                        {counts.accepted ? ` · ${counts.accepted} yes` : ""}
                        {counts.tentative ? ` · ${counts.tentative} maybe` : ""}
                        {counts.declined ? ` · ${counts.declined} no` : ""}
                        {counts.needs_action ? ` · ${counts.needs_action} awaiting` : ""}
                    </div>
                    <ul className="invite-guests">
                        {guests.map(g => (
                            <li key={g.email} title={g.rsvpComment ?? undefined}>
                                <StatusIcon status={g.rsvpStatus} />
                                <span className="invite-guest-name">{g.name || g.email}</span>
                                {g.role === "organizer" && <span className="invite-guest-tag">Organizer</span>}
                                {g.email.toLowerCase() === myEmail && <span className="invite-guest-tag">You</span>}
                            </li>
                        ))}
                    </ul>
                </dd>
            </dl>

            {canRespond && (
                <div className="invite-rsvp">
                    <span className="invite-rsvp-label">Going?</span>
                    <div className="invite-rsvp-buttons" role="group" aria-label="RSVP">
                        {(["accepted", "tentative", "declined"] as const).map(response => (
                            <Button
                                key={response}
                                size="sm"
                                variant={myResponse === response ? "primary" : "secondary"}
                                aria-pressed={myResponse === response}
                                disabled={respond.isPending}
                                onClick={() => respond.mutate(response)}
                            >
                                {response === "accepted" ? "Yes" : response === "tentative" ? "Maybe" : "No"}
                            </Button>
                        ))}
                    </div>
                    {!noteOpen && <button type="button" className="invite-link-button" onClick={() => setNoteOpen(true)}>Add a note</button>}
                    {myResponse !== "needs_action" && <span className="invite-rsvp-current">You replied: {RESPONSE_LABEL[myResponse]}</span>}
                    {noteOpen && (
                        <div className="invite-note">
                            <textarea
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder={`Note to ${invite.organizer?.name || "the organizer"} (sent with your reply)`}
                                rows={2}
                                maxLength={2000}
                            />
                        </div>
                    )}
                    <label className="invite-notify">
                        <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
                        Send response to organizer
                    </label>
                </div>
            )}
            {isRequest && !cancelled && !canRespond && !query.isLoading && !state?.superseded && (
                <p className="invite-footnote muted">None of your addresses is on this invite's guest list, so you can't RSVP from here.</p>
            )}
        </section>
    );
}
